import { normalizeConditionName } from './skinConditionNormalization';
import { CLASS_COLORS_MAP, getColorByClassId } from './skinResultsUtils';

const SKIN_CONCERN_NAMES = ['Hydration', 'Oxygen', 'Smoothness', 'Dullness', 'UnevenSkin'];

export function isSkinConcernName(name) {
  return SKIN_CONCERN_NAMES.includes(name);
}

const CONDITION_MAPPING = {
  hydration: 'Hydration',
  oxygen: 'Oxygen',
  smoothness: 'Smoothness',
  dullness: 'Dullness',
  uneven_skintone: 'UnevenSkin',
};

function flattenDetections(detectionsByView = {}) {
  const merged = [];
  Object.values(detectionsByView).forEach((list) => {
    if (Array.isArray(list)) merged.push(...list);
  });
  return merged;
}

/**
 * Build unified concerns list from skin report payload (same shape as SkinRoutinePage concernsList).
 */
export function buildConcernsListFromReport(report, {
  showConcerns = true,
  showConditions = true,
  allowedNames = null,
} = {}) {
  if (!report) return [];

  const skinScore = report.skin_score || report.skinScore || {};
  const labelScores = skinScore?.skin_label_scores;

  if (labelScores && typeof labelScores === 'object' && Object.keys(labelScores).length > 0) {
    return Object.entries(labelScores)
      .filter(([name]) => {
        const className = normalizeConditionName(name);
        if (!showConcerns && isSkinConcernName(className)) return false;
        if (!showConditions && !isSkinConcernName(className)) return false;
        return allowedNames === null || allowedNames.has(className);
      })
      .map(([class_name, score]) => {
        let raw = score;
        if (raw && typeof raw === 'object') {
          raw = raw.score ?? raw.value ?? raw.percentage ?? 0;
        }
        const numScore = typeof raw === 'number' ? raw : parseFloat(raw) || 0;
        const key = normalizeConditionName(class_name);
        return {
          class_name: key,
          score: Math.round(numScore),
          color: CLASS_COLORS_MAP[key] || 'rgb(140, 80, 200)',
          confidence: 1,
          count: 1,
          class_id: null,
          is_condition: isSkinConcernName(key),
        };
      })
      .sort((a, b) => a.score - b.score);
  }

  const grouped = flattenDetections(report.detections || {}).reduce((acc, det) => {
    const className = normalizeConditionName(det?.class_name || det?.className || 'Unknown');
    if (!showConcerns && isSkinConcernName(className)) return acc;
    if (!showConditions && !isSkinConcernName(className)) return acc;
    if (allowedNames !== null && !allowedNames.has(className)) return acc;
    const confidence = det?.confidence ?? det?.confidence_score ?? 0;
    const score = det?.score !== undefined ? det.score : 100;
    if (!acc[className]) {
      acc[className] = {
        class_name: className,
        confidences: [],
        scores: [],
        count: 0,
        class_id: det?.class_id ?? det?.classId ?? null,
      };
    }
    acc[className].confidences.push(confidence);
    acc[className].scores.push(score);
    acc[className].count += 1;
    return acc;
  }, {});

  const combinedDetections = Object.values(grouped)
    .filter((g) => g.class_id !== 8 && g.class_id !== 7 && g.class_name !== 'Unknown' && g.class_name !== 'Mole'
      && (allowedNames === null || allowedNames.has(g.class_name)))
    .map((group) => {
      const scores = group.scores?.length ? group.scores : [100];
      const combinedScore = Math.min(...scores);
      const avgConfidence = group.confidences.reduce((s, c) => s + c, 0) / group.confidences.length;
      const color = CLASS_COLORS_MAP[group.class_name]
        || getColorByClassId(group.class_id ?? 0);
      return {
        class_name: group.class_name,
        score: Math.round(combinedScore),
        confidence: avgConfidence,
        count: group.count,
        color,
        class_id: group.class_id,
      };
    });

  const conditionItems = [];
  const skinConditions = report.condition_scores || report.skinConditions || {};
  Object.entries(CONDITION_MAPPING).forEach(([key, displayName]) => {
    if (!showConcerns) return;
    if (allowedNames !== null && !allowedNames.has(displayName)) return;
    const entry = skinConditions[key];
    const scoreVal = typeof entry === 'object' ? entry?.score : entry;
    if (scoreVal !== undefined && scoreVal !== null) {
      conditionItems.push({
        class_name: displayName,
        score: Math.round(Number(scoreVal) || 0),
        confidence: 1,
        count: 1,
        color: CLASS_COLORS_MAP[displayName] || 'rgb(140, 80, 200)',
        class_id: null,
        is_condition: true,
      });
    }
  });

  return [...combinedDetections, ...conditionItems].sort((a, b) => a.score - b.score);
}

export function getOverallSkinScore(report) {
  const skinScore = report?.skin_score || report?.skinScore || {};
  const raw = skinScore?.final_skin_score ?? skinScore?.score ?? null;
  if (raw == null || Number.isNaN(Number(raw))) return null;
  return Math.max(0, Math.min(100, Math.round(Number(raw))));
}

export function formatAnalysisDate(value) {
  if (!value) return '--';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '--';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(parsed);
}
